export type ServiceContent = {
  slug: string;
  title: string;
  shortDescription: string;
  tagline: string;
  intro: string;
  highlights: { title: string; copy: string }[];
  details: { heading: string; copy: string }[];
  goodFor: string[];
};

export const SERVICES: ServiceContent[] = [
  {
    slug: "metal-braces",
    title: "Metal Braces",
    shortDescription:
      "Stainless-steel brackets and wires remain a popular choice — proven, effective, and dependable for every kind of smile.",
    tagline: "The classic, dependable way to a confident smile.",
    intro:
      "Today's metal braces are smaller, smoother, and more comfortable than ever. Stainless-steel brackets are bonded directly to the front of each tooth and connected with a high-tech archwire that gradually guides your teeth into their ideal position.",
    highlights: [
      {
        title: "Time-tested results",
        copy: "Metal braces can correct nearly any orthodontic concern — from minor crowding to complex bite issues.",
      },
      {
        title: "Lower profile, more comfort",
        copy: "Modern brackets are noticeably smaller and rounder than the braces you may remember from years past.",
      },
      {
        title: "Personality, optional",
        copy: "Choose from a rainbow of elastic colors at every adjustment visit — or stick with subtle clear or silver.",
      },
    ],
    details: [
      {
        heading: "How it works",
        copy: "At each visit, Dr. Amy makes small, precise adjustments to the wire connecting your brackets. Over time, gentle pressure encourages your teeth into their planned position — typically over 12–24 months, depending on your treatment plan.",
      },
      {
        heading: "Caring for your braces",
        copy: "Brushing after meals, flossing daily with a threader or water flosser, and avoiding hard, sticky, or chewy foods will keep your braces working smoothly. We'll send you home with everything you need at your bonding appointment.",
      },
    ],
    goodFor: [
      "Complex bite corrections",
      "Significant crowding or spacing",
      "Younger patients who like color choices",
      "Patients who want a solution that works continuously without relying on daily wear habits",
    ],
  },
  {
    slug: "clear-brackets",
    title: "Clear Brackets",
    shortDescription:
      "Virtually invisible ceramic brackets paired with clear elastomeric ties, designed to move teeth comfortably with discreet, beautiful results.",
    tagline: "All the power of braces — with a fraction of the look.",
    intro:
      "Clear ceramic brackets work just like traditional metal braces, but blend in beautifully with your natural tooth color. They're a wonderful option for adults and image-conscious teens who want the predictability of braces with a much subtler appearance.",
    highlights: [
      {
        title: "Discreet by design",
        copy: "Tooth-colored brackets virtually disappear against your enamel — perfect for photos, video calls, and everyday life.",
      },
      {
        title: "Smooth and stain-resistant",
        copy: "Modern ceramics are polished and resist staining, even from coffee, tea, and red wine.",
      },
      {
        title: "Same proven approach",
        copy: "Treatment time and results are comparable to traditional braces, with the same precision adjustments.",
      },
    ],
    details: [
      {
        heading: "Is this right for me?",
        copy: "Clear brackets are an excellent choice for most adults and teens who don't want their treatment to be the first thing people notice. During your consultation, Dr. Amy will help determine whether ceramic brackets are the best match for your bite and goals.",
      },
      {
        heading: "Mixed approaches",
        copy: "Some patients choose clear brackets on the upper teeth (most visible when smiling) and metal on the lower — saving on cost while keeping the look subtle.",
      },
    ],
    goodFor: [
      "Adults looking for the most esthetic braces solution",
      "Image-conscious teens",
      "Professionals who present or appear on camera",
      "Patients who aren't interested in removable aligners that require daily wear habits",
    ],
  },
  {
    slug: "clear-aligners",
    title: "Clear Aligners",
    shortDescription:
      "A modern approach to straightening teeth using a custom series of smooth, comfortable, and virtually invisible aligner trays.",
    tagline: "A removable, comfortable path to your perfect smile.",
    intro:
      "Clear aligners use a series of custom-fit, transparent trays with tooth colored attachments to make targeted adjustments to your smile. Many patients are surprised to learn that complex orthodontic problems can often be treated successfully with clear aligners. As an orthodontic specialist with extensive experience in advanced aligner therapy, Dr. Amy has helped patients achieve outstanding results in cases involving severe crowding, bite discrepancies, extractions, and even surgical jaw correction. If you've been told you're not a candidate for clear aligners, it may be worth seeking a second opinion; Dr. Amy routinely treats challenging orthodontic cases using the latest innovations in clear aligner therapy.",
    highlights: [
      {
        title: "Comfortable and low-profile",
        copy: "Smooth plastic trays sit close to your teeth — no brackets, no wires, no tongue irritation.",
      },
      {
        title: "Predictable progress",
        copy: "Each tray makes small, planned movements, so you'll see steady, measurable change over time.",
      },
      {
        title: "Easy oral hygiene",
        copy: "Pop them out to brush and floss as you always have — no special tools or routines required.",
      },
    ],
    details: [
      {
        heading: "How they're made",
        copy: "We use 3D digital scans to design and produce each tray, custom fit to your teeth. Unlike practices that rely primarily on automated treatment simulations, Dr. Amy personally designs and oversees every stage of your aligner treatment, carefully planning each individual tooth movement to achieve the most precise and predictable results possible.",
      },
      {
        heading: "Wear schedule",
        copy: "Trays are worn 20–22 hours a day and are switched out weekly. Most patients complete treatment within 6–18 months, depending on the complexity of their case.",
      },
    ],
    goodFor: [
      "Minor, Moderate and Complex cases",
      "Patients who prefer a discreet treatment option that fits seamlessly into your personal and professional life",
      "Those who value the flexibility of removing your appliance to eat, drink, brush, and floss without restrictions",
      "Patients who are motivated to wear their aligners consistently and want a comfortable, convenient alternative to braces",
    ],
  },
  {
    slug: "early-interceptive-orthodontics",
    title:
      "Early Interceptive Orthodontics – Rapid Palatal Expansion and Airway Orthodontics",
    shortDescription:
      "Removable or fixed expanders help address crowding and sleep disordered breathing by gently widening the jaw to increase airway space and make room for proper tooth alignment.",
    tagline: "Guiding growth early for healthier airways and room to smile.",
    intro:
      "Early interceptive orthodontics focuses on guiding jaw development while a child is still growing — often preventing more complex treatment later on. Using upper and lower orthodontic expanders, we can gently widen the jaws to create space for crowded teeth and improve the airway. A broader, properly developed jaw supports better breathing, which can have a meaningful impact on a child's sleep, focus, and overall well-being.",
    highlights: [
      {
        title: "Upper and lower expanders",
        copy: "We use both palatal (upper) and mandibular (lower) expanders — removable or fixed — tailored to each patient's anatomy and needs.",
      },
      {
        title: "Airway-focused care",
        copy: "Widening the jaw can increase airway volume, helping address crowding alongside concerns related to sleep-disordered breathing.",
      },
      {
        title: "Best while still growing",
        copy: "Children and early teens benefit most because developing jaws respond beautifully to gentle, guided expansion.",
      },
    ],
    details: [
      {
        heading: "How expanders work",
        copy: "An expander attaches to the back teeth and uses a small screw or spring mechanism to gradually widen the jaw over several weeks. A brief daily adjustment at home applies gentle pressure — most children adapt quickly. Once the desired width is reached, the device stays in place while new bone stabilizes.",
      },
      {
        heading: "Beyond straight teeth",
        copy: "Orthodontics isn't just about alignment. By addressing narrow arches and restricted airways early, we aim to support better nasal breathing, more restful sleep, and healthier facial development — benefits that extend well beyond the smile itself.",
      },
    ],
    goodFor: [
      "Crossbites and significant crowding",
      "Narrow upper or lower arches",
      "Children showing signs of sleep-disordered breathing",
      "Young patients whose jaws are still developing",
    ],
  },
  {
    slug: "touch-up-orthodontics",
    title: "Touch-up Orthodontics",
    shortDescription:
      "Have your teeth shifted since braces or aligners? Our Touch-Up plan offers a simple, cost-effective way to correct minor relapse and restore your confident smile.",
    tagline: "Small shifts, simple fixes — get your smile back on track.",
    intro:
      "Life happens — and sometimes teeth drift after orthodontic treatment. Whether you've noticed a little crowding, spacing, or a tooth that's shifted out of place, our Touch-Up Orthodontics program is designed to re-treat relapse cases efficiently. With shorter treatment times and lower fees than a full course of braces or aligners, touch-up care gets you back to the smile you worked hard for.",
    highlights: [
      {
        title: "Flexible options",
        copy: "Depending on your needs, touch-up treatment may involve clear aligners, limited braces, or a retainer-based approach.",
      },
      {
        title: "Shorter timelines",
        copy: "Because we're correcting minor movement rather than starting from scratch, most touch-up cases finish in a fraction of the time.",
      },
      {
        title: "Cost-effective",
        copy: "Touch-up plans are priced to reflect the scope of correction — making it easier to address relapse before it worsens.",
      },
    ],
    details: [
      {
        heading: "What to expect",
        copy: "We'll start with a consultation to assess how much your teeth have shifted and recommend the simplest path back to alignment. Many patients are surprised how quickly a few months of targeted treatment can restore their results.",
      },
      {
        heading: "Don't wait on relapse",
        copy: "Teeth have a natural tendency to drift over time, especially without consistent retainer wear. The sooner minor shifts are addressed, the simpler and more affordable the correction. Call us at the first sign of movement.",
      },
    ],
    goodFor: [
      "Minor crowding or spacing after previous treatment",
      "Post-orthodontic relapse",
      "Adults wanting a quick refresh",
      "Patients looking for a shorter, lower-cost correction",
    ],
  },
  {
    slug: "permanent-retainers",
    title: "Permanent Retainers",
    shortDescription:
      "Protect your investment in your smile with a permanent retainer, designed to keep your teeth aligned long after treatment is complete.",
    tagline: "Lock in your results — for good.",
    intro:
      "Dr. Amy strongly believes that we should do everything in our power to protect the investment you've made in your smile after orthodontic treatment is complete. Permanent retainers — thin, custom-bent wires bonded behind the teeth — provide continuous, worry-free protection without relying on daily wear habits. We offer upper and lower custom-bent permanent retainers designed to keep your teeth aligned for a lifetime.",
    highlights: [
      {
        title: "Always working",
        copy: "Unlike removable retainers, bonded retainers work around the clock — no remembering to put them in at night.",
      },
      {
        title: "Custom-bent for your smile",
        copy: "Each wire is individually shaped and fitted to the contours of your teeth for a precise, comfortable fit.",
      },
      {
        title: "Upper and lower options",
        copy: "We provide custom permanent retainers for both the upper and lower arches, based on your treatment plan and retention needs.",
      },
    ],
    details: [
      {
        heading: "Why permanent retention matters",
        copy: "Teeth naturally want to shift throughout life. A permanent retainer is one of the most reliable ways to prevent relapse and preserve the alignment you achieved — protecting both your smile and the time and effort you invested in treatment.",
      },
      {
        heading: "Caring for your retainer",
        copy: "Permanent retainers require regular brushing and flossing around the wire, which we'll show you how to do at your appointment. We'll also check your retainer at routine visits to make sure it stays secure and effective.",
      },
    ],
    goodFor: [
      "Anyone finishing orthodontic treatment",
      "Patients who want maximum protection against relapse",
      "Those who prefer not to depend on removable retainers",
      "Anyone committed to preserving their smile long-term",
    ],
  },
];

export function getServiceBySlug(slug: string | undefined): ServiceContent | undefined {
  if (!slug) return undefined;
  return SERVICES.find((s) => s.slug === slug);
}
